// src/components/AppTopBar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { initializeApp, getApps } from "firebase/app";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { getDatabase, ref, onValue, off } from "firebase/database";

import UElogo from "../assets/UElogo_horizontal.png";
import { EMAILJS } from "../config/emailConfig";
import { fetchAdminLevels, getAdminLevel } from "../utils/userAccess";

import {
  House,
  CalendarClock,
  CalendarSearch,
  CloudUpload,
  LogOut,
  Bell,
  Sheet,
  Printer,
  SquareCheckBig,
} from "lucide-react";

// --- Firebase (safe init if app already exists) ---
const firebaseConfig = {
  apiKey: "AIzaSyAaR9ZU1w_bFxMKMbNxu-9eBpuOOGm1Eow",
  authDomain: "bookingappv1-d1e2d.firebaseapp.com",
  databaseURL: "https://bookingappv1-d1e2d-default-rtdb.firebaseio.com/",
  projectId: "bookingappv1-d1e2d",
  storageBucket: "bookingappv1-d1e2d.appspot.com",
  messagingSenderId: "134532713601",
  appId: "1:134532713601:web:5ad89fec3f4bf09b519180",
};
if (!getApps().length) initializeApp(firebaseConfig);

/**
 * Reusable top bar with:
 * - Left: logo
 * - Right: optional approvals bell (approver or L1 only) with pending badge
 * - Right: dropdown menu (closes on outside click / Esc)
 *
 * Props (all optional):
 * - showBell: boolean (default true)
 * - className: extra classes for the bar container
 */
export default function AppTopBar({ showBell = true, className = "", onExportXLSX,
  onExportPDF, MyReservationsMenu = true, AllReservationsMenu = true}) {
  const navigate = useNavigate();

  const [authUser, setAuthUser] = useState(null);
  const [adminLevel, setAdminLevel] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [showMenu, setShowMenu] = useState(false);

  const menuRef = useRef(null);

  // Auth + admin level
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      setAuthUser(u || null);
      if (u?.email) {
        try {
          const map = await fetchAdminLevels();
          setAdminLevel(getAdminLevel(u.email, map));
        } catch {
          setAdminLevel(null);
        }
      } else {
        setAdminLevel(null);
      }
    });
    return () => unsub();
  }, []);

  // Approver = approver email OR Level 1
  const isApprover = useMemo(() => {
  return adminLevel === 0 || adminLevel === 1;
}, [adminLevel]);

  // Pending approvals badge (only if approver + showBell)
  useEffect(() => {
    if (!isApprover || !showBell) return;
    const db = getDatabase();
    const approvalsRef = ref(db, "approvalRequests");
    const cb = (snap) => {
      const val = snap.val() || {};
      const count = Object.values(val).filter((r) => r?.status === "pending").length;
      setPendingCount(count);
    };
    onValue(approvalsRef, cb);
    return () => off(approvalsRef, "value", cb);
  }, [isApprover, showBell]);

  // Close dropdown on outside click / Esc
  useEffect(() => {
    const closeIfOutside = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setShowMenu(false);
    };
    const closeOnEsc = (e) => {
      if (e.key === "Escape") setShowMenu(false);
    };
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("touchstart", closeIfOutside, { passive: true });
    window.addEventListener("keydown", closeOnEsc);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("touchstart", closeIfOutside);
      window.removeEventListener("keydown", closeOnEsc);
    };
  }, []);

  const handleSignOut = () => {
    const auth = getAuth();
    signOut(auth).then(() => navigate("/"));
  };

  const goTo = (path) => () => navigate(path);

  return (
    <div
      className={`w-full flex justify-between items-center px-6 py-4 absolute top-0 left-0 ${className}`}
    >
      {/* Left: Logo */}
      <img src={UElogo} alt="UE Logo" className="h-12 w-auto" />

      {/* Right: Bell + Menu */}
      <div className="flex gap-4 items-center relative">
        {/* 🔔 Approvals bell (only approver or Level 1) */}
        {showBell && isApprover && (
          <button
            onClick={goTo("/approvals")}
            className="relative bg-gray-600 text-white px-2 py-1 hover:bg-gray-700 text-sm w-fit shadow-md rounded"
            title="Approvals"
          >
            <Bell className="w-6 h-6" />
            {pendingCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full px-1">
                {pendingCount}
              </span>
            )}
          </button>
        )}

        {/* ☰ Dropdown */}
        <div ref={menuRef} className="relative menu-dropdown">
          <button
            onClick={() => setShowMenu((v) => !v)}
            className="bg-gray-600 text-white px-2 py-1 hover:bg-gray-700 text-sm w-fit shadow-md rounded"
            title="Menu"
            aria-expanded={showMenu}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-50">
              <button onClick={goTo("/buildings")} className="block w-full text-left px-4 py-2 text-white hover:bg-gray-700">
                <div className="flex gap-2">
                  <House className="w-5 h-5 text-white" />
                  Home
                </div>
              </button>
              
              {MyReservationsMenu && (
              <button onClick={goTo("/my-reservations")} className="block w-full text-left px-4 py-2 text-white hover:bg-gray-700">
                <div className="flex gap-2">
                  <CalendarClock className="w-5 h-5 text-white" />
                  My Reservations
                </div>
              </button>
              )}

              {onExportXLSX && (
  <button
    onClick={onExportXLSX}
    className="block w-full text-left px-4 py-2 text-white hover:bg-gray-700"
  >
    <div className="flex gap-2">
      <Sheet className="w-5 h-5 text-white" />
      Create XLSX
    </div>
  </button>
)}

{onExportPDF && (
  <button
    onClick={onExportPDF}
    className="block w-full text-left px-4 py-2 text-white hover:bg-gray-700"
  >
    <div className="flex gap-2">
      <Printer className="w-5 h-5 text-white" />
      Print PDF
    </div>
  </button>
)}

              {AllReservationsMenu && [0, 1, 2].includes(adminLevel) && (
                <button onClick={goTo("/admin-reservations")} className="block w-full text-left px-4 py-2 text-white hover:bg-gray-700">
                  <div className="flex gap-2 text-purple-500">
                    <CalendarSearch className="w-5 h-5 text-purple-500" />
                    All Reservations
                  </div>
                </button>
              )}

              {adminLevel === 0 && (
                <button onClick={goTo("/admin-upload")} className="block w-full text-left px-4 py-2 text-white hover:bg-gray-700">
                  <div className="flex gap-2 text-purple-500">
                    <CloudUpload className="w-5 h-5 text-purple-500" />
                    Upload
                  </div>
                </button>
              )}

              <button
                onClick={handleSignOut}
                className="block w-full text-left px-4 py-2 text-red-400 hover:bg-gray-700"
              >
                <div className="flex gap-2">
                  <LogOut className="w-5 h-5 text-red-400" />
                  Sign Out
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

